"use client";

import { useTranslations } from "next-intl";
import { hasRealNewsThumb, newsHref } from "@/lib/news-image";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Newspaper,
  Search as SearchIcon,
  Calendar,
  Plus,
  Pencil,
  LayoutGrid,
  List,
  ArrowUpDown,
  ChevronDown,
  MoreVertical,
  Eye,
  EyeOff,
  Ban,
  Trash2,
} from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import dynamic from "next/dynamic";
import { getNewsArticles, getNewsByTopic, newsSubCategory } from "@/services/news";
import { newsHasTopic, TOPIC_TAG_RULES, type NewsTopic } from "@/lib/news-topics";
import { useAdmin } from "@/hooks/use-admin";
import { cleanExcerpt } from "@/lib/sanitize";
import type { NewsArticle } from "@/types/news";
import { cn } from "@/lib/utils";
import { generateInitialsSvgDataUrl } from "@/lib/thumbnail-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NewsEditorDialog = dynamic(() => import("@/components/admin/news-editor-dialog"), { ssr: false });

// ─── Constants ─────────────────────────────────────────────────────────────────

const categoryColors: Record<string, string> = {
  news: "bg-blue-600 text-white border-blue-700",
  press: "bg-purple-600 text-white border-purple-700",
  release: "bg-green-600 text-white border-green-700",
  interview: "bg-amber-500 text-black border-amber-600",
  feature: "bg-pink-600 text-white border-pink-700",
  announcement: "bg-red-600 text-white border-red-700",
  industry: "bg-slate-700 text-white border-slate-800",
};

const categories = ["all", "news", "press", "release", "interview", "feature", "announcement", "industry"];

// Allowed topic-chip vocabulary — the curated brand / item / genre / topic tags
// the scraper emits, plus Breakin Bread. Anything else (artist / user names on
// in-house posts) is excluded so the topic filter stays about SUBJECTS, not
// people (Gordon: "remove Artist or User names, Keep Breakin Bread").
const TOPIC_VOCAB = new Set<string>([
  // Brands
  "Ableton", "Native Instruments", "Pioneer DJ", "Serato", "Roland", "Korg", "Moog",
  "Arturia", "Behringer", "Universal Audio", "Pro Tools", "FL Studio", "Logic Pro",
  "Spotify", "Apple Music", "YouTube", "TikTok", "Bandcamp", "SoundCloud",
  // Items
  "Plugin", "Synth", "Sampler", "Drum Machine", "DAW", "Controller", "Microphone",
  "Monitors", "Headphones", "Interface", "Turntable", "Mixer", "Mobile", "DJ", "VJ", "Mixing",
  // Genres
  "Hip-Hop", "Techno", "House", "Jazz", "Funk", "Soul", "Reggae", "Drum & Bass", "Ambient",
  // Topics
  "AI", "Royalties", "Streaming", "Innovation", "Legal", "Live & Touring", "Vinyl",
  "Free", "Tutorial", "Review", "Deal",
  // DAWs + open-source tools (sub-filters on the DAW / Open Source tabs)
  ...TOPIC_TAG_RULES.map(r => r.tag),
  // In-house
  "Breakin Bread", "Breakin' Bread",
]);
// Topic words shown in the reader's language (brands, gear models, genres and
// DAW names stay as they are).
const TAG_KEY: Record<string, string> = {
  Free: 'free', Tutorial: 'tutorial', Review: 'review', Deal: 'deal', Royalties: 'royalties', Streaming: 'streaming',
  Innovation: 'innovation', Legal: 'legal', 'Live & Touring': 'live', Vinyl: 'vinyl', Monitors: 'monitors',
  Headphones: 'headphones', Interface: 'interface', Turntable: 'turntable', Mixer: 'mixer', Mobile: 'mobile', Mixing: 'mixing',
};

type ViewMode = "grid" | "list";
type SortMode = "newest" | "oldest" | "title";

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(timestamp: any): string {
  if (!timestamp) return "";
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function sortArticles(articles: NewsArticle[], sort: SortMode): NewsArticle[] {
  const toMs = (v: any): number => {
    if (!v) return 0;
    if (v?.toMillis) return v.toMillis();
    if (v?.toDate) return v.toDate().getTime();
    if (typeof v === 'string') return new Date(v).getTime() || 0;
    return 0;
  };
  return [...articles].sort((a, b) => {
    if (sort === "title") return (a.title || "").localeCompare(b.title || "");
    const ta = toMs(a.publishedAt);
    const tb = toMs(b.publishedAt);
    return sort === "newest" ? tb - ta : ta - tb;
  });
}

// ─── Article Card ─────────────────────────────────────────────────────────────

function ArticleCard({
  article,
  viewMode,
  isAdmin,
  isSuperAdmin,
  onEdit,
  onHide,
  onErase,
  onBlockSource,
  onBlockKeyword,
  tDraft,
  tBy,
  tReadMore,
}: {
  article: NewsArticle;
  viewMode: ViewMode;
  isAdmin: boolean;
  isSuperAdmin?: boolean;
  onEdit: () => void;
  onHide?: (hidden: boolean) => void;
  onErase?: () => void;
  onBlockSource?: () => void;
  onBlockKeyword?: () => void;
  tDraft: string;
  tBy: (author: string) => string;
  tReadMore: string;
}) {
  const isHidden = !!(article as any).adminHidden;
  const isScraped = (article as any).category === 'industry';
  // Three-tier thumbnail: real image → article video poster → generated
  // initials SVG seeded from the title so cards never render a blank tile.
  const realImage = (article as any).imageUrl || article.featuredImageUrl || (article as any).thumbnailUrl;
  const fallbackImage = generateInitialsSvgDataUrl(article.title || 'News', 512);
  // Swap to the generated fallback if the remote image URL is broken (set but
  // 404s at the source) — otherwise the tile renders blank (Gordon: "missing
  // a thumbnail in news").
  const [imgFailed, setImgFailed] = useState(false);
  const imageUrl = imgFailed || !realImage ? fallbackImage : realImage;
  const videoUrl = (article as any).videoUrl as string | undefined;
  const href = newsHref(article);

  const videoRef = useRef<HTMLVideoElement>(null);
  const handleMouseEnter = useCallback(() => {
    if (videoRef.current) { videoRef.current.play().catch(() => {}); }
  }, []);
  const handleMouseLeave = useCallback(() => {
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
  }, []);

  if (viewMode === "list") {
    return (
      <div className="relative group/card">
        <Link href={href} className="flex gap-4 items-start p-3 rounded-xl border border-border/50 bg-card hover:bg-card/80 transition-all hover:border-border">
          {/* Square thumbnail — shows video on hover if videoUrl is set */}
          <div
            className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-muted"
            onMouseEnter={videoUrl ? handleMouseEnter : undefined}
            onMouseLeave={videoUrl ? handleMouseLeave : undefined}
          >
            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                muted={false}
                playsInline
                loop
                className="absolute inset-0 w-full h-full object-cover z-10"
                style={{ display: 'none' }}
                onPlay={e => { (e.currentTarget.style.display = 'block'); }}
                onPause={e => { (e.currentTarget.style.display = 'none'); }}
              />
            )}
            {imageUrl ? (
              <Image src={imageUrl} alt={article.title} fill className="object-cover" onError={() => setImgFailed(true)} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Newspaper className="h-6 w-6 text-muted-foreground/30" />
              </div>
            )}
          </div>
          {/* Content */}
          <div className="flex-grow min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {article.category && (
                <Badge className={cn("capitalize text-xs border", categoryColors[article.category] || "bg-secondary")}>
                  {article.category}
                </Badge>
              )}
              {article.status === "draft" && (
                <Badge className="bg-yellow-500 text-black border-yellow-600 text-xs border">{tDraft}</Badge>
              )}
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />{formatDate(article.publishedAt)}
              </span>
            </div>
            <h2 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors">
              {article.title}
            </h2>
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {cleanExcerpt(article.excerpt || article.content || "", 120)}
            </p>
          </div>
        </Link>
        {isHidden && <Badge className="absolute top-2 left-2 bg-black/70 text-white text-[10px]">Hidden</Badge>}
        {isAdmin && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
            <Button variant="secondary" size="icon" className="h-7 w-7 shadow-md" onClick={(e) => { e.preventDefault(); onEdit(); }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {isSuperAdmin && (
              <ModerationMenu
                isHidden={isHidden} isScraped={isScraped}
                onHide={onHide} onErase={onErase} onBlockSource={onBlockSource} onBlockKeyword={onBlockKeyword}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Grid card
  return (
    <div className="relative group/card">
      <Link href={href} className="group block h-full">
        <Card className="overflow-hidden border-border/50 bg-card hover:bg-card/80 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 h-full flex flex-col">
          {/* Square image */}
          <div
            className="relative aspect-square w-full bg-muted overflow-hidden"
            onMouseEnter={videoUrl ? handleMouseEnter : undefined}
            onMouseLeave={videoUrl ? handleMouseLeave : undefined}
          >
            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                muted={false}
                playsInline
                loop
                className="absolute inset-0 w-full h-full object-cover z-10"
                style={{ display: 'none' }}
                onPlay={e => { (e.currentTarget.style.display = 'block'); }}
                onPause={e => { (e.currentTarget.style.display = 'none'); }}
              />
            )}
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={article.title}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
                onError={() => setImgFailed(true)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Newspaper className="h-12 w-12 text-muted-foreground/20" />
              </div>
            )}
            <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
              {article.category && (
                <Badge className={cn("capitalize text-xs border", categoryColors[article.category] || "bg-secondary")}>
                  {article.category}
                </Badge>
              )}
              {article.status === "draft" && (
                <Badge className="bg-yellow-500 text-black border-yellow-600 text-xs border">{tDraft}</Badge>
              )}
            </div>
          </div>

          <CardContent className="p-4 flex-grow flex flex-col">
            <h2 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors mb-2 flex-grow">
              {article.title}
            </h2>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {cleanExcerpt(article.excerpt || article.content || "", 150)}
            </p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(article.publishedAt)}
              </span>
              {(article as any).author?.displayName && (
                <span className="truncate">{tBy((article as any).author.displayName)}</span>
              )}
            </div>
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {article.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
      {isHidden && (
        <Badge className="absolute top-3 left-3 z-10 bg-black/70 text-white text-[10px]">Hidden</Badge>
      )}
      {isAdmin && (
        <div className="absolute top-3 right-3 z-10 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
          <Button variant="secondary" size="icon" className="h-8 w-8 shadow-md" onClick={(e) => { e.preventDefault(); onEdit(); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {isSuperAdmin && (
            <ModerationMenu
              isHidden={isHidden} isScraped={isScraped}
              onHide={onHide} onErase={onErase} onBlockSource={onBlockSource} onBlockKeyword={onBlockKeyword}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Super-admin moderation dropdown (hide / erase + learning blocklist).
function ModerationMenu({ isHidden, isScraped, onHide, onErase, onBlockSource, onBlockKeyword }: {
  isHidden: boolean; isScraped: boolean;
  onHide?: (hidden: boolean) => void; onErase?: () => void; onBlockSource?: () => void; onBlockKeyword?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="icon" className="h-8 w-8 shadow-md" onClick={(e) => e.preventDefault()}>
          <MoreVertical className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={(e) => { e.preventDefault(); onHide?.(!isHidden); }}>
          {isHidden ? <><Eye className="h-4 w-4 mr-2" /> Unhide</> : <><EyeOff className="h-4 w-4 mr-2" /> Hide</>}
        </DropdownMenuItem>
        {isScraped && (
          <DropdownMenuItem onClick={(e) => { e.preventDefault(); onBlockSource?.(); }}>
            <Ban className="h-4 w-4 mr-2" /> Hide + block this source
          </DropdownMenuItem>
        )}
        {isScraped && (
          <DropdownMenuItem onClick={(e) => { e.preventDefault(); onBlockKeyword?.(); }}>
            <Ban className="h-4 w-4 mr-2" /> Block a keyword…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className="text-destructive" onClick={(e) => { e.preventDefault(); onErase?.(); }}>
          <Trash2 className="h-4 w-4 mr-2" /> Erase permanently
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NewsPage() {
  const t = useTranslations("news");
  const tCommon = useTranslations("common");
  const tHome = useTranslations("home");

  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<NewsArticle[]>([]);
  /** Top-level tab: 'bb' = Breakin' Bread originals; music-tech / music-industry = the scraped feeds split
   *  Tech / Business; daw / open-source = cross-cutting topics (a story can be in both); 'all' = everything. */
  const [feedTab, setFeedTab] = useState<'bb' | 'music-tech' | 'music-industry' | NewsTopic | 'all'>('all');
  // Topic tabs reach further back than the newest-100 list: their stories are
  // loaded once per visit when the tab is first opened.
  const [topicArticles, setTopicArticles] = useState<Partial<Record<NewsTopic, NewsArticle[]>>>({});
  const activeTopic: NewsTopic | null = feedTab === 'daw' || feedTab === 'open-source' ? feedTab : null;
  const isTopicTab = activeTopic !== null;
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeTag, setActiveTag] = useState<string>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null);
  const { isAdmin, isSuperAdmin } = useAdmin();

  // Scraped trade-press cards without a thumbnail are boring (Gordon) — hide
  // them entirely; the cron backfills og:images so they resurface once enriched.
  const withThumbs = (list: NewsArticle[]) =>
    list.filter((a: any) => a.category !== 'industry' || a.imageUrl || a.featuredImageUrl || a.thumbnailUrl);

  // ── Super-admin moderation (hide / erase + learning blocklist) ──
  const handleHide = useCallback(async (a: NewsArticle, hidden: boolean) => {
    const { setNewsHidden } = await import("@/services/news-moderation");
    try {
      await setNewsHidden(a.id, hidden);
      setArticles(prev => prev.map(x => x.id === a.id ? { ...x, adminHidden: hidden } as any : x));
    } catch { /* rules will reject non-admins */ }
  }, []);
  const handleErase = useCallback(async (a: NewsArticle) => {
    if (!window.confirm(`Erase "${a.title}" permanently?`)) return;
    const mod = await import("@/services/news-moderation");
    try {
      // Goes through the Admin-SDK endpoint now, so it actually deletes. Only
      // remove the row from the UI AFTER a confirmed success — never optimistic
      // (that's what made erases "come back" when the delete was rejected).
      await mod.eraseNewsArticle(a.id);
      setArticles(prev => prev.filter(x => x.id !== a.id));
      // Learn: also block this source so the scraper won't re-add it.
      const src = (a as any).source;
      if (src && window.confirm(`Also block the source "${String(src).replace(/^industry-rss:/, '')}" from future scrapes?`)) {
        await mod.blockNewsSource(src);
      }
    } catch (err: any) {
      window.alert(`Could not erase: ${err?.message || 'unknown error'}. Nothing was removed.`);
    }
  }, []);
  const handleBlockSource = useCallback(async (a: NewsArticle) => {
    const src = (a as any).source;
    if (!src) return;
    const { blockNewsSource } = await import("@/services/news-moderation");
    await blockNewsSource(src).catch(() => {});
    await handleHide(a, true);
  }, [handleHide]);
  const handleBlockKeyword = useCallback(async (a: NewsArticle) => {
    const kw = window.prompt("Block future articles containing which keyword?", "");
    if (!kw?.trim()) return;
    const { blockNewsKeyword } = await import("@/services/news-moderation");
    await blockNewsKeyword(kw).catch(() => {});
  }, []);

  // Only show articles with a REAL representative thumbnail (or a video that
  // renders as one). Junk images — WordPress ™/emoji tiles, source logos —
  // count as "no thumbnail" (shared rule with the scraper in lib/news-image).
  const hasRealCard = useCallback((a: any) =>
    hasRealNewsThumb(a.imageUrl || a.featuredImageUrl || a.thumbnailUrl) || !!a.videoUrl || !!a.bbVideo,
  []);

  useEffect(() => {
    async function loadNews() {
      try {
        const data = withThumbs(await getNewsArticles(100)).filter(hasRealCard);
        setArticles(data);
        setFilteredArticles(data);
      } catch (err) {
        console.error("Error loading news:", err);
      } finally {
        setIsLoading(false);
      }
    }
    loadNews();
  }, [hasRealCard]);

  const reloadArticles = async () => {
    const data = withThumbs(await getNewsArticles(100)).filter(hasRealCard);
    setArticles(data);
  };

  useEffect(() => {
    if (!activeTopic || topicArticles[activeTopic]) return;
    let cancelled = false;
    getNewsByTopic(activeTopic).then(list => {
      if (!cancelled) setTopicArticles(prev => ({ ...prev, [activeTopic]: withThumbs(list).filter(hasRealCard) }));
    }).catch(() => { if (!cancelled) setTopicArticles(prev => ({ ...prev, [activeTopic]: [] })); });
    return () => { cancelled = true; };
    // withThumbs is a stable pure filter; topicArticles is read only as a cache check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopic, hasRealCard]);

  /** Everything the current top tab can show — topic tabs merge the newest
   *  list with the topic query (deduped by id). */
  const tabPool = React.useMemo(() => {
    if (!activeTopic) return articles;
    const byId = new Map<string, NewsArticle>();
    for (const a of [...articles, ...(topicArticles[activeTopic] || [])]) byId.set(a.id, a);
    return [...byId.values()].filter(a => newsHasTopic(a, activeTopic));
  }, [articles, topicArticles, activeTopic]);
  const topicLoading = !!activeTopic && !topicArticles[activeTopic];

  useEffect(() => {
    let filtered = tabPool;
    // Top-tab gate: BB originals (everything that's NOT industry) vs scraped Music Industry feeds vs both.
    const subCatOf = newsSubCategory;
    // Hidden articles are invisible to everyone except super admins (who see
    // them dimmed with a badge so they can unhide/erase).
    if (!isSuperAdmin) filtered = filtered.filter((a: any) => !a.adminHidden);
    if (feedTab === 'bb') filtered = filtered.filter(a => a.category !== 'industry');
    else if (feedTab === 'music-tech') filtered = filtered.filter(a => subCatOf(a) === 'music-tech');
    else if (feedTab === 'music-industry') filtered = filtered.filter(a => subCatOf(a) === 'music-industry');
    if (activeCategory !== "all") {
      filtered = filtered.filter((a) => a.category === activeCategory);
    }
    if (activeTag !== "all") {
      filtered = filtered.filter((a: any) => Array.isArray(a.tags) && a.tags.includes(activeTag));
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title?.toLowerCase().includes(term) ||
          a.excerpt?.toLowerCase().includes(term) ||
          a.tags?.some((t) => t.toLowerCase().includes(term))
      );
    }
    setFilteredArticles(sortArticles(filtered, sortMode));
  }, [tabPool, activeCategory, searchTerm, sortMode, feedTab, activeTag, isSuperAdmin]);

  const openEditor = (article: NewsArticle | null) => {
    setEditingArticle(article);
    setEditorOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Newspaper className="h-12 w-12 text-muted-foreground animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">{t("loading")}</p>
        </div>
      </div>
    );
  }

  const sortLabel: Record<SortMode, string> = {
    newest: t("sortNewest"),
    oldest: t("sortOldest"),
    title: t("sortTitle"),
  };

  const categoryLabel: Record<string, string> = {
    all: t("catAll"),
    news: t("catNews"),
    press: t("catPress"),
    release: t("catRelease"),
    interview: t("catInterview"),
    feature: t("catFeature"),
    announcement: t("catAnnouncement"),
    industry: "Industry",
  };

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Sticky header ── */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-[var(--header-height)] z-30">
        <div className="w-full px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Title */}
            <div className="flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold">{t("title")}</h1>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative w-full sm:w-48 md:w-64">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="search"
                  placeholder={t("searchPlaceholder")}
                  className="pl-9 h-9 bg-secondary/50"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Sort */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 gap-1">
                    <ArrowUpDown className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{sortLabel[sortMode]}</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(Object.keys(sortLabel) as SortMode[]).map((s) => (
                    <DropdownMenuItem key={s} onClick={() => setSortMode(s)} className={cn(sortMode === s && "font-medium text-primary")}>
                      {sortLabel[s]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* View toggle */}
              <div className="flex rounded-md border border-border overflow-hidden h-9">
                <button
                  className={cn("px-2.5 flex items-center transition-colors", viewMode === "grid" ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
                  onClick={() => setViewMode("grid")}
                  title={tCommon("gridView")}
                  aria-label={tCommon("gridView")}
                  aria-pressed={viewMode === "grid"}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  className={cn("px-2.5 flex items-center transition-colors border-l border-border", viewMode === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
                  onClick={() => setViewMode("list")}
                  title={tCommon("listView")}
                  aria-label={tCommon("listView")}
                  aria-pressed={viewMode === "list"}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>

              {/* Admin: new article button */}
              {isAdmin && (
                <Button size="sm" className="h-9" onClick={() => openEditor(null)}>
                  <Plus className="h-4 w-4 mr-1" /> {t("addArticle")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Top tabs: All / BB News / Tech / Business / DAW / Open Source ──
           "Music Tech" was previously labelled "External"/"Industry" but the
           scraped feed list was wider music journalism (Pitchfork, Rolling
           Stone, etc.). Per Gordon: "all those scraped music news should be
           a little more music tech oriented and with its own filter in news."
           The cron now pulls from music-tech / production / DAW / royalty /
           AI-in-music sources, and the tab label matches. The Firestore
           category stays 'industry' so existing scraped rows still resolve.
           DAW and Open Source (Gordon 2026-10-31) are topics, not feeds: one
           story can sit in both (an Ardour release) and still be in Tech.
           The row scrolls sideways on phones instead of wrapping. */}
      <div className="w-full px-3 sm:px-4 pt-3">
        <div role="group" aria-label={t("filterTabs")} className="flex gap-1 rounded-lg border border-border bg-secondary/30 p-1 w-fit max-w-full overflow-x-auto hide-scrollbar">
          {/* Same words as the Home news rail (Gordon 2026-10-31: "change
              inhouse label to BB News to be consistent with the home page"). */}
          {([
            { id: 'all',            label: tHome('newsFilterAll') },
            { id: 'bb',             label: tHome('newsFilterBb') },
            { id: 'music-tech',     label: tHome('newsFilterTech') },
            { id: 'music-industry', label: tHome('newsFilterBusiness') },
            { id: 'daw',            label: tHome('newsFilterDaw') },
            { id: 'open-source',    label: tHome('newsFilterOpenSource') },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={feedTab === tab.id}
              onClick={() => { setFeedTab(tab.id); setActiveCategory('all'); setActiveTag('all'); }}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                feedTab === tab.id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tag sub-menu — logical categories derived from article tags
             (brands / items / genres / topics from the scraper's tagger) ── */}
      {(() => {
        const tabArticles = tabPool.filter((a: any) => {
          if (feedTab === 'bb') return a.category !== 'industry';
          if (feedTab === 'all' || isTopicTab) return true;
          return a.category === 'industry';
        });
        const counts = new Map<string, number>();
        tabArticles.forEach((a: any) => (Array.isArray(a.tags) ? a.tags : [])
          // Only real subject tags — excludes feed slugs, 'industry', and
          // artist/user names on in-house posts (keeps Breakin Bread).
          .filter((t: string) => TOPIC_VOCAB.has(t))
          .forEach((t: string) => counts.set(t, (counts.get(t) || 0) + 1)));
        const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 14);
        if (top.length === 0) return null;
        return (
          <div className="w-full px-3 sm:px-4 pt-2">
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
              <Button size="sm" variant={activeTag === 'all' ? 'default' : 'outline'} className="h-7 text-[11px] whitespace-nowrap" onClick={() => setActiveTag('all')}>
                {t('allTopics')}
              </Button>
              {top.map(([tag, n]) => (
                <Button key={tag} size="sm" variant={activeTag === tag ? 'default' : 'outline'} className="h-7 text-[11px] whitespace-nowrap" onClick={() => setActiveTag(activeTag === tag ? 'all' : tag)}>
                  {TAG_KEY[tag] ? t(`tagLabel.${TAG_KEY[tag]}`) : tag} <span className="ml-1 opacity-60">{n}</span>
                </Button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Category filter strip ── */}
      <div className="w-full px-3 sm:px-4 py-3">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {categories
            // Hide 'industry' chip in BB tab and BB-only chips in Industry tab
            .filter((cat) => {
              if (cat === 'all') return true;
              if (feedTab === 'bb') return cat !== 'industry';
              if (feedTab === 'music-tech' || feedTab === 'music-industry') return cat === 'industry';
              return true;
            })
            .map((cat) => (
            <Button
              key={cat}
              variant={activeCategory === cat ? "default" : "outline"}
              size="sm"
              className="capitalize whitespace-nowrap h-8 text-xs"
              onClick={() => setActiveCategory(cat)}
              data-tap-info={cat === "all" ? "All categories" : `Category: ${cat}`}
            >
              {categoryLabel[cat] ?? cat}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Articles ── */}
      <div className="w-full px-3 sm:px-4 pb-12">
        {filteredArticles.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className={cn("h-16 w-16 text-muted-foreground/30 mx-auto mb-4", topicLoading && "animate-pulse")} />
            {/* Topic tabs fill day by day — say so instead of a bare "nothing found". */}
            <p className="text-muted-foreground">{topicLoading ? t("loading") : isTopicTab && !searchTerm.trim() && activeTag === 'all' && activeCategory === 'all' ? t("topicEmpty") : t("noNewsFound")}</p>
            {isAdmin && (
              <Button size="sm" className="mt-4" onClick={() => openEditor(null)}>
                <Plus className="h-4 w-4 mr-1" /> Write the first article
              </Button>
            )}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} viewMode="grid" isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} onEdit={() => openEditor(article)} onHide={(h) => handleHide(article, h)} onErase={() => handleErase(article)} onBlockSource={() => handleBlockSource(article)} onBlockKeyword={() => handleBlockKeyword(article)} tDraft={tCommon("draft")} tBy={(author) => t("writtenBy", { author })} tReadMore={t("readMore")} />
            ))}
          </div>
        ) : (
          <div className="space-y-2 w-full">
            {filteredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} viewMode="list" isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} onEdit={() => openEditor(article)} onHide={(h) => handleHide(article, h)} onErase={() => handleErase(article)} onBlockSource={() => handleBlockSource(article)} onBlockKeyword={() => handleBlockKeyword(article)} tDraft={tCommon("draft")} tBy={(author) => t("writtenBy", { author })} tReadMore={t("readMore")} />
            ))}
          </div>
        )}
      </div>

      {/* ── Admin editor dialog ── */}
      {isAdmin && (
        <NewsEditorDialog
          isOpen={editorOpen}
          onClose={() => { setEditorOpen(false); setEditingArticle(null); }}
          article={editingArticle}
          onSaved={reloadArticles}
          onDeleted={reloadArticles}
        />
      )}
    </div>
  );
}
